import { MUTATE, MUTATE_MULTIPLE } from '@/store/modules/mutation_types';
import WarroomService from '@/services/WarroomService';
import moment from 'moment';

const { PagesService, PageHistoryService } = WarroomService;

function nextHistoryRequestId(state) {
  return (Number.isSafeInteger(state.historyRequestId) ? state.historyRequestId : 0) + 1;
}

function nextAccountPagesRequestId(state) {
  return (Number.isSafeInteger(state.accountPagesRequestId) ? state.accountPagesRequestId : 0) + 1;
}

export function getAccountPages({ state, commit }, { selectedAccount }) {
  const accountPagesRequestId = nextAccountPagesRequestId(state);
  const historyRequestId = nextHistoryRequestId(state);
  commit(MUTATE_MULTIPLE, [{
    property: 'accountPagesRequestId',
    with: accountPagesRequestId,
  }, {
    property: 'historyRequestId',
    with: historyRequestId,
  }, {
    property: 'loadingHistory',
    with: true,
  }, {
    property: 'historyIsLoaded',
    with: false,
  }, {
    property: 'historyCursor',
    with: null,
  }, {
    property: 'historyHasMore',
    with: false,
  }, {
    property: 'loadingMoreHistory',
    with: false,
  }, {
    property: 'historyLoadMoreError',
    with: false,
  }, {
    property: 'requestStatus',
    with: 'loading',
  }]);

  const { id: accountId } = selectedAccount;

  return PagesService.find({ url: `/${accountId}` })
    .then(data => {
      if (state.accountPagesRequestId !== accountPagesRequestId) return state.pages;
      const { message = [] } = data;
      commit(MUTATE_MULTIPLE, [
        {
          property: 'requestStatus',
          with: 'success',
        }, {
          property: 'pages',
          with: message,
        },
      ]);
      if (message.length >= 1) {
        commit(MUTATE_MULTIPLE, [{
          property: 'activePage',
          with: message[0],
        }]);
        getPageHistory({ state, commit }, { selectedAccount });
      }
      commit(MUTATE, {
        property: 'loadingHistory',
        with: false,
      });
      commit(MUTATE, {
        property: 'requestStatus',
        with: 'empty',
      });

      return Promise.resolve(message);
    }).catch((e) => {
      if (state.accountPagesRequestId !== accountPagesRequestId) return state.pages;
      console.error(e);
      commit(MUTATE, {
        property: 'requestStatus',
        with: 'error',
      });
    });
}

export function setPage({ state, commit }, { selectedPage }) {
  const historyRequestId = nextHistoryRequestId(state);
  commit(MUTATE_MULTIPLE, [{
    property: 'historyRequestId',
    with: historyRequestId,
  }, {
    property: 'activePage',
    with: selectedPage,
  }, {
    property: 'loadingHistory',
    with: true,
  }, {
    property: 'historyIsLoaded',
    with: false,
  }, {
    property: 'historyCursor',
    with: null,
  }, {
    property: 'historyHasMore',
    with: false,
  }, {
    property: 'loadingMoreHistory',
    with: false,
  }]);
}

function isHistoryPage(result) {
  return result && Array.isArray(result.items) && typeof result.has_more === 'boolean';
}

function hasNextHistoryCursor(result) {
  return !result.has_more || typeof result.next_cursor === 'string';
}

function parseHistoryPage(data) {
  const result = data && data.message;
  if (Array.isArray(result)) {
    return { items: result, nextCursor: null, hasMore: false };
  }
  if (!isHistoryPage(result)) {
    throw new Error('Resposta inválida ao buscar histórico da página.');
  }
  if (!hasNextHistoryCursor(result)) {
    throw new Error('Cursor ausente ao buscar próxima página do histórico.');
  }
  return { items: result.items, nextCursor: result.next_cursor, hasMore: result.has_more };
}

function formatHistory(items) {
  return items.map(item => {
    const createdAt = moment(item.created_at).add(-3, 'hour').valueOf();
    return {
      ...item,
      created_at: createdAt,
      created_at_formatted: moment(createdAt).format('DD/MM/YYYY HH:mm:ss'),
    };
  });
}

function historyRequestUrl(pageId, accountId, cursor) {
  const cursorParam = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
  return `/${pageId}/${accountId}?limit=100${cursorParam}`;
}

export function getPageHistory({ state, commit }, { selectedAccount }) {
  const historyRequestId = nextHistoryRequestId(state);
  commit(MUTATE_MULTIPLE, [{
    property: 'historyRequestId',
    with: historyRequestId,
  }, {
    property: 'requestStatus',
    with: 'loading',
  }, {
    property: 'history',
    with: [],
  }, {
    property: 'historyCursor',
    with: null,
  }, {
    property: 'historyHasMore',
    with: false,
  }, {
    property: 'historyLoadMoreError',
    with: false,
  }, {
    property: 'loadingMoreHistory',
    with: false,
  }]);

  const { id: accountId } = selectedAccount;
  const { id: pageId } = state.activePage;

  return PageHistoryService.find({ url: historyRequestUrl(pageId, accountId) })
    .then(data => {
      if (state.historyRequestId !== historyRequestId) return state.history;
      const page = parseHistoryPage(data);
      const message = formatHistory(page.items);
      commit(MUTATE_MULTIPLE, [{
        property: 'requestStatus',
        with: 'success',
      }, {
        property: 'history',
        with: message,
      }, {
        property: 'historyCursor',
        with: page.nextCursor,
      }, {
        property: 'historyHasMore',
        with: page.hasMore,
      }, {
        property: 'loadingHistory',
        with: false,
      }, {
        property: 'historyIsLoaded',
        with: true,
      }]);
      return message;
    }).catch((e) => {
      if (state.historyRequestId !== historyRequestId) return state.history;
      console.error(e);
      commit(MUTATE_MULTIPLE, [{
        property: 'requestStatus',
        with: 'error',
      }, {
        property: 'historyIsLoaded',
        with: false,
      }, {
        property: 'loadingHistory',
        with: false,
      }]);
    });
}

export function getMorePageHistory({ state, commit }, { selectedAccount }) {
  if (!state.historyHasMore || !state.historyCursor || state.loadingMoreHistory) {
    return Promise.resolve(state.history);
  }

  commit(MUTATE_MULTIPLE, [{
    property: 'loadingMoreHistory',
    with: true,
  }, {
    property: 'historyLoadMoreError',
    with: false,
  }]);

  const { id: accountId } = selectedAccount;
  const { id: pageId } = state.activePage;
  const cursor = state.historyCursor;
  const historyRequestId = state.historyRequestId;
  return PageHistoryService.find({
    url: historyRequestUrl(pageId, accountId, cursor),
  }).then(data => {
    if (state.historyRequestId !== historyRequestId || state.activePage.id !== pageId ||
      state.historyCursor !== cursor) return state.history;
    const page = parseHistoryPage(data);
    const message = formatHistory(page.items);
    commit(MUTATE_MULTIPLE, [{
      property: 'history',
      with: state.history.concat(message),
    }, {
      property: 'historyCursor',
      with: page.nextCursor,
    }, {
      property: 'historyHasMore',
      with: page.hasMore,
    }, {
      property: 'loadingMoreHistory',
      with: false,
    }]);
    return state.history;
  }).catch((e) => {
    if (state.historyRequestId !== historyRequestId || state.activePage.id !== pageId ||
      state.historyCursor !== cursor) return state.history;
    console.error(e);
    commit(MUTATE_MULTIPLE, [{
      property: 'historyLoadMoreError',
      with: true,
    }, {
      property: 'loadingMoreHistory',
      with: false,
    }]);
  });
}
